import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/base/MovieCard";
import MovieCardSkeleton from "@/components/base/MovieCardSkeleton";
import {
  movies,
  genreOptions,
  regionOptions,
  yearOptions,
  type MovieType,
} from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

const sortOptions = [
  { key: "latest", label: "最新" },
  { key: "rating", label: "评分" },
  { key: "hot", label: "热门" },
];

const PAGE_SIZE = 10;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get("type") === "tv" ? "tv" : "movie";

  const [type, setType] = useState<MovieType>(initialType);
  const [genre, setGenre] = useState(searchParams.get("genre") || "全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState(searchParams.get("sort") || "latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);

  const typeLabel = type === "movie" ? "电影" : "电视剧";

  usePageMeta({
    title:
      type === "movie"
        ? "嫩草影院电影大全 - 动作/爱情等类型影片在线观看"
        : "嫩草影院电视剧大全 - 国产/欧美等热播剧集在线观看",
    description:
      type === "movie"
        ? "嫩草影院电影大全汇集动作、爱情、科幻、悬疑等类型影片，支持按地区与年代筛选，快速找到想看的高清电影。"
        : "嫩草影院电视剧大全收录国产剧、欧美剧、日韩剧等热播剧集，提供分类筛选与完整剧集资料，追剧更省心。",
    keywords: `嫩草影院,${typeLabel}大全,${typeLabel}在线观看,高清${typeLabel},${typeLabel}分类`,
  });

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [type, genre, region, year, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, genre, region, year, sort]);

  const handleTypeChange = (next: MovieType) => {
    setType(next);
    setGenre("全部");
    setSearchParams(next === "movie" ? {} : { type: next });
  };

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("latest");
  };

  const filtered = useMemo(() => {
    const list = movies.filter((movie) => {
      if (movie.type !== type) return false;
      if (genre !== "全部" && !movie.genres.includes(genre)) return false;
      if (region !== "全部" && movie.region !== region) return false;
      if (year !== "全部" && String(movie.year) !== year) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "hot") return b.popularity - a.popularity;
      return b.year - a.year;
    });
  }, [type, genre, region, year, sort]);

  const visibleList = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-[72px]">
        <div className="w-full bg-background-300 border-b border-background-400/60">
          <div className="w-full px-4 md:px-8 pt-6 pb-7 md:pt-8 md:pb-9">
            <nav className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">{typeLabel}分类</span>
            </nav>
            <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {typeLabel}大全
                </h1>
                <p className="mt-2 text-sm text-foreground-600">
                  按类型、地区、年代三维度筛选，共收录 {movies.filter((m) => m.type === type).length} 部演示
                  {typeLabel}资料
                </p>
              </div>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-400/70 self-start md:self-auto">
                {(["movie", "tv"] as MovieType[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => handleTypeChange(item)}
                    className={`px-5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      type === item
                        ? "bg-primary-500 text-white"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {item === "movie" ? "电影" : "电视剧"}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
              {genreOptions.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setGenre(item)}
                  className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors cursor-pointer ${
                    genre === item
                      ? "bg-accent-500 border-accent-500 text-foreground-50"
                      : "bg-background-100 border-background-400/70 text-foreground-600 hover:border-primary-400 hover:text-foreground-950"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 rounded-lg bg-background-100 border border-background-300/80 px-4 py-3.5 md:px-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
              {[
                { label: "类型", value: genre, options: genreOptions, onChange: setGenre },
                { label: "地区", value: region, options: regionOptions, onChange: setRegion },
                { label: "年代", value: year, options: yearOptions, onChange: setYear },
              ].map((field) => (
                <label key={field.label} className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 shrink-0">{field.label}</span>
                  <span className="relative flex-1">
                    <select
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                      className="w-full h-9 pl-3 pr-8 rounded-md bg-background-200 border border-background-400/80 text-xs text-foreground-900 appearance-none outline-none focus:border-primary-500 cursor-pointer"
                    >
                      {field.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500 text-sm pointer-events-none" />
                  </span>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between lg:justify-end gap-3">
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-200 border border-background-400/70">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`px-3.5 py-1 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      sort === option.key
                        ? "bg-secondary-500 text-foreground-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-background-200 hover:bg-background-300 border border-background-400/70 text-xs text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                重置
              </button>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-foreground-500">
              共找到 <span className="text-primary-500 font-semibold">{filtered.length}</span> 部
              {typeLabel}
              {genre !== "全部" && ` · ${genre}`}
              {region !== "全部" && ` · ${region}`}
              {year !== "全部" && ` · ${year}`}
            </p>
            <span className="hidden sm:inline text-xs text-foreground-500">
              当前展示 {visibleList.length} 部
            </span>
          </div>

          <div className="mt-4">
            {loading ? (
              <MovieCardSkeleton count={10} />
            ) : visibleList.length === 0 ? (
              <div className="py-20 rounded-lg bg-background-100 border border-background-300/70 text-center">
                <i className="ri-search-eye-line text-3xl text-foreground-500" />
                <p className="mt-3 text-sm text-foreground-800">没有找到符合条件的影视</p>
                <p className="mt-1.5 text-xs text-foreground-500">
                  试着减少筛选条件，或者回到全部{typeLabel}
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 h-10 px-5 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  重置筛选条件
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                {visibleList.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            )}
          </div>

          {!loading && hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="h-11 px-8 inline-flex items-center gap-2 rounded-md bg-background-100 hover:bg-background-200 border border-background-400 text-sm text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" />
                加载更多（剩余 {filtered.length - visible} 部）
              </button>
            </div>
          )}

          {!loading && !hasMore && visibleList.length > 0 && (
            <p className="mt-8 text-center text-xs text-foreground-500">
              已经到底了，共 {filtered.length} 部作品
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}